"use client"

import { useState, useEffect } from "react"
import Dashboard from "../../components/dashboard/Dashboard"
import config from "../../config"
import { ToastContainer, toast } from "react-toastify"
import { Calculator, Download, Save } from "lucide-react"
import axios from "axios"
import ValidationErrorSidebar from '../../components/ui/ValidationErrorSidebar';
import { validationPatterns as centralizedPatterns } from '../../utils/validationPatterns';
import { usePageSave } from '../../contexts/PageSaveContext';

export default function CapitalGainsForm() {
  const { markPageAsSaved, markPageAsUnsaved } = usePageSave()
  const [salesConsideration, setSalesConsideration] = useState("")
  const [costOfAcquisition, setCostOfAcquisition] = useState("")
  const [capitalGains, setCapitalGains] = useState(0)
  const [panNo, setPanNo] = useState(localStorage.getItem("PAN_NO") || "")
  const [assessmentYear, setAssessmentYear] = useState(localStorage.getItem("YEAR") || "")
  const [isLoading, setIsLoading] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [capitalGainIncomeId, setCapitalGainIncomeId] = useState(null)
  const [validationErrors, setValidationErrors] = useState({});

  // Calculate capital gains whenever inputs change
  useEffect(() => {
    const sales = Number.parseFloat(salesConsideration) || 0
    const cost = Number.parseFloat(costOfAcquisition) || 0
    const gains = sales - cost
    setCapitalGains(gains > 0 ? gains : 0)
  }, [salesConsideration, costOfAcquisition])

  // Fetch prefilled data on component mount
  useEffect(() => {
    const fetchPrefilledData = async () => {
      if (!panNo || !assessmentYear) {
        toast.error("Please enter PAN No and Assessment Year", {
          position: "top-right",
          autoClose: 5000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
          progress: undefined,
        })
        return
      }

      setIsLoading(true)
      try {
        const response = await fetch(`${config.apiUrl}/income/capitalgain/${panNo}/${assessmentYear}`)
        if (response.ok) {
          const data = await response.json()
          setSalesConsideration(data.capitalGainIncome.totSaleCnsdrn || "")
            setCostOfAcquisition(data.capitalGainIncome.totCstAcqisn || "")
            setCapitalGainIncomeId(data.capitalGainIncome.capitalGainIncomeId || null)
            toast.success("Prefilled data loaded successfully")
        }
      } catch (error) {
        console.error("Error fetching prefilled data:", error)
        toast.info("No prefilled data found")
      } finally {
        setIsLoading(false)
      }
    }

    fetchPrefilledData()
  }, [panNo, assessmentYear])

  const formatNumber = (amount) => {
    return new Intl.NumberFormat("en-IN", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(amount)
  }

  const onSubmit = async (e) => {
    e.preventDefault()

    // Allow saving with zero values - only check if fields are not empty strings
    if (salesConsideration === "" || costOfAcquisition === "") {
      toast.error("Please fill in all required fields")
      return
    }

    // Show alert if capital gains exceed 125000
    if (capitalGains > 125000) {
      toast.warning("In ITR 1, the maximum gains as per Section 112A can be INR 1,25,000", {
        position: "top-right",
        autoClose: 8000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
      });
      
      // You can choose to return here if you don't want to submit when gains exceed 125000
      return;
    }

    const requestBody = {
      capitalGainIncome: {
        capitalGainIncomeId: capitalGainIncomeId,
        totSaleCnsdrn: salesConsideration,
        totCstAcqisn: costOfAcquisition,
        longCap112A: capitalGains,
      },
    }

    setIsSubmitting(true)
    try {
      const response = await axios.patch(`${config.apiUrl}/income/capitalgain/${panNo}/${assessmentYear}`,requestBody)
      const saved = markPageAsSaved('capital-gain')
      if (saved) {
        toast.success("Data submitted successfully")
      } else {
        toast.error("Please save your profile first before saving capital gain details!")
      }
      
    } catch (error) {
      console.error("Error:", error)
      toast.error("An error occurred while submitting data")
    } finally {
      setIsSubmitting(false)
    }
  }

  // Prepare capital gains comparison data
  const capitalGainsComparisonData = {
    totalCapitalGains: capitalGains,
    salesConsideration: Number.parseFloat(salesConsideration) || 0,
    costOfAcquisition: Number.parseFloat(costOfAcquisition) || 0,
    oldRegimeTaxable: capitalGains, // Capital gains is same in both regimes
    newRegimeTaxable: capitalGains, // Capital gains is same in both regimes
    difference: 0, // No difference for capital gains between regimes
    formatCurrency: (value) => {
      return new Intl.NumberFormat("en-IN", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }).format(Math.abs(value))
    },
  }

  // Add validation function for PAN
  const validateField = (field, value) => {
    if (field === 'panNo') {
      const pattern = centralizedPatterns.PAN;
      const stringValue = (value || '').trim();
      let error = '';

      // Only show error if field is filled and length matches pattern
      if (stringValue && stringValue.length === 10 && !pattern.test(stringValue)) {
        error = 'Invalid PAN format. Expected format: ABCDE1234F';
      }
      return error;
    }
    return '';
  };

  // Update the PAN change handler to include validation
  const handlePanChange = (value) => {
    setPanNo(value);
    
    // Validate PAN field
    const error = validateField('panNo', value);
    if (error) {
      setValidationErrors(prev => ({
        ...prev,
        panNo: error
      }));
    } else {
      setValidationErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors.panNo;
        return newErrors;
      });
    }
  };

  return (
    <Dashboard 
      salaryData={capitalGainsComparisonData}
      showTaxComparison={true}
    >
      <div className="min-h-screen bg-gradient-to-br from-cyan-50 to-blue-50 p-4 sm:p-6 lg:p-8">
        <div className="max-w-4xl ">
          {/* Header Card */}
          <div className="bg-white rounded-xl shadow-lg border border-cyan-100 mb-6">
            <div className="bg-gradient-to-r from-cyan-600 to-cyan-700 text-white p-6 rounded-t-xl">
              <div className="flex items-center gap-3">
                <Calculator className="h-6 w-6" />
                <div>
                  <h1 className="text-xl sm:text-2xl font-bold">Capital Gains</h1>
                  <p className="text-cyan-100 text-sm mt-1">
                    Long Term capital gains u/s 112A not chargeable to Income-tax
                  </p>
                </div>
              </div>
            </div>

            {/* Form */}
            <form onSubmit={onSubmit} className="p-6 space-y-6">
              {isLoading && (
                <div className="bg-cyan-50 border border-cyan-200 rounded-lg p-4">
                  <div className="flex items-center gap-2 text-cyan-700">
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-cyan-600"></div>
                    <span className="text-sm">Loading prefilled data...</span>
                  </div>
                </div>
              )}

              {/* Input Fields */}
              <div className="grid gap-6 md:gap-8">
                <div className="space-y-2 flex items-center justify-between">
                  <label className="block text-sm font-medium text-gray-700">
                    Total Sales Consideration <span className="text-red-500">*</span>
                  </label>
                  <div className="relative">
                    <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500">₹</span>
                    <input
                      type="number"
                      placeholder="Enter sales consideration amount"
                      value={salesConsideration}
                      onChange={(e) => {
                        markPageAsUnsaved('capital-gain')
                        // Only allow numbers (0-9) and decimal point
                        const value = e.target.value.replace(/[^0-9.]/g, '');
                        // Prevent multiple decimal points
                        const parts = value.split('.');
                        const filteredValue = parts.length > 2 ? parts[0] + '.' + parts.slice(1).join('') : value;
                        const numericValue = Math.max(0, Number.parseFloat(filteredValue || 0)); // Ensure non-negative
                        setSalesConsideration(numericValue);
                      }}
                      className="w-full pl-8 pr-4 py-3 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500 transition-colors"
                      required
                    />
                  </div>
                </div>

                <div className="space-y-2 flex items-center justify-between">
                  <label className="block text-sm font-medium text-gray-700">
                    Total Cost of Acquisition <span className="text-red-500">*</span>
                  </label>
                  <div className="relative">
                    <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500">₹</span>
                    <input
                      type="number"
                      placeholder="Enter cost of acquisition amount"
                      value={costOfAcquisition}
                      onChange={(e) => {
                        markPageAsUnsaved('capital-gain')
                        // Only allow numbers (0-9) and decimal point
                        const value = e.target.value.replace(/[^0-9.]/g, '');
                        // Prevent multiple decimal points
                        const parts = value.split('.');
                        const filteredValue = parts.length > 2 ? parts[0] + '.' + parts.slice(1).join('') : value;
                        const numericValue = Math.max(0, Number.parseFloat(filteredValue || 0)); // Ensure non-negative
                        setCostOfAcquisition(numericValue);
                      }}
                      className="w-full pl-8 pr-4 py-3 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500 transition-colors"
                      required
                    />
                  </div>
                </div>
              </div>

              {/* Result Section */}
              <div className="bg-gradient-to-r from-cyan-50 to-blue-50 border border-cyan-200 rounded-lg p-6">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900">Long Term Capital Gains (Section 112A)</h3>
                    <p className="text-sm text-gray-600 mt-1">Calculated automatically based on your inputs</p>
                  </div>
                  <div className="text-right">
                    <div className="text-3xl font-bold text-cyan-700">₹{formatNumber(capitalGains)}</div>
                    <div className="text-sm text-gray-500 mt-1">{capitalGains > 0 ? "Taxable Amount" : "No Gains"}</div>
                  </div>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex flex-col sm:flex-row gap-4 pt-4">
                <button
                  type="submit"
                  disabled={isSubmitting || salesConsideration === "" || costOfAcquisition === ""}
                  className="flex-1 flex items-center justify-center gap-2 px-6 py-3 bg-gradient-to-r from-cyan-600 to-cyan-700 text-white rounded-lg hover:from-cyan-700 hover:to-cyan-800 focus:outline-none focus:ring-2 focus:ring-cyan-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
                >
                  {isSubmitting ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                      Submitting...
                    </>
                  ) : (
                    <>
                      <Save className="h-4 w-4" />
                      Submit Capital Gains
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
      <ToastContainer />
      <ValidationErrorSidebar
        errors={validationErrors}
        isVisible={Object.keys(validationErrors).length > 0}
        onClose={() => {}}
        onFieldClick={(fieldKey) => {
          const inputElement = document.querySelector(`input[name="${fieldKey}"]`);
          if (inputElement) {
            inputElement.focus();
            inputElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
          }
        }}
        title="Capital Gain Validation Errors"
      />
    </Dashboard>
  )
}